/* eslint-disable no-unused-vars */
import { Download, Inventory as InventoryIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Alert, Box, Button, Card, CardActionArea, CardContent, CardMedia, CircularProgress, IconButton, MenuItem, Pagination, TextField, Tooltip, Typography } from "@mui/material"; // Material UI components for loading spinner and text
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PiTreeStructureFill } from "react-icons/pi";
import { useLocation, useNavigate } from "react-router-dom";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import MachineCard from "../../components/MachineCard/MachineCard";
import { useDebounce } from "../../hooks/useDebounce";
import { getMachinesByIds, getMachinesWithModels, getSiteLines } from "../../services/myMachineService";
import { getAssignedMachineIdsForSite } from "../../services/documentService";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import ModelIcon from "../../components/ModelIcon/ModelIcon";
import ModelPDFIcon from "../../components/ModelPDFIcon/ModelPDFIcon";
import PageTitle from "../../components/PageTitle/PageTitle";
import { ASSETS_BASE_URL } from "../../config";
import { ROUTES } from "../../constants";
import { useSite } from "../../context/siteContext";
import "./MyMachines.css";

const MyMachines = () => {
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isSearching, setIsSearching] = useState(false); // NEW: Track search loading separately
	const [error, setError] = useState(null);
	const [pageSize, setPageSize] = useState(50); // Default page size
	const [currentPage, setCurrentPage] = useState(1); // Default current page
	const [totalPages, setTotalPages] = useState(0); // Total number of pages based on pageSize and totalItems
	const [machineSearchTerm, setMachineSearchTerm] = useState("");
	const [linesPage, setLinesPage] = useState(1);
	const [linesPageSize, setLinesPageSize] = useState(50);
	const [lineSearchTerm, setLineSearchTerm] = useState("");
	const [siteLines, setSiteLines] = useState([]);
	const [lineDocumentationById, setLineDocumentationById] = useState({});
	const [activeSiteLineId, setActiveSiteLineId] = useState(null);
	const [lineSelectionMode, setLineSelectionMode] = useState(true);
	const { selectedSite, siteData, selectedSiteData, currencySymbol } = useSite();
	const [iconsLoadingById, setIconsLoadingById] = useState({});
	const { t } = useTranslation();
	const debouncedSearchTerm = useDebounce(machineSearchTerm, 500);
	const [sortOption, setSortOption] = useState(t('GENERAL.NUMBER_ASC'));
	const sortOptions = [t('GENERAL.NAME_ASC'), t('GENERAL.NAME_DESC'), t('GENERAL.NUMBER_ASC'), t('GENERAL.NUMBER_DESC')];
	const pageSizeOptions = [25, 50, 100];
	const navigate = useNavigate();
	const location = useLocation();
	const previousSelectedSiteRef = useRef(selectedSite);
	const { stockImageUrl } = useBrandingAssets();
	const lineFallbackImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
	const selectedSiteLineId = useMemo(() => {
		return new URLSearchParams(location.search).get("site_line_id") || "";
	}, [location.search]);
	const normalizedSelectedSiteData = useMemo(() => {
		if (!selectedSiteData || Array.isArray(selectedSiteData)) return null;
		return selectedSiteData;
	}, [selectedSiteData]);
	const filteredLines = useMemo(() => {
		if (!lineSearchTerm.trim()) return siteLines;
		const lower = lineSearchTerm.trim().toLowerCase();
		return siteLines.filter((line) =>
			(line.line_name || "").toLowerCase().includes(lower) ||
			String(line.line_number || "").toLowerCase().includes(lower) ||
			(line.customer_site_name || "").toLowerCase().includes(lower) ||
			(line.customer_name || "").toLowerCase().includes(lower)
		);
	}, [siteLines, lineSearchTerm]);
	const paginatedLines = useMemo(() => {
		const start = (linesPage - 1) * linesPageSize;
		return filteredLines.slice(start, start + linesPageSize);
	}, [filteredLines, linesPage, linesPageSize]);
	const linesTotalPages = useMemo(() => Math.ceil(filteredLines.length / linesPageSize), [filteredLines.length, linesPageSize]);

	const isGlobalSite = selectedSite === "GLOBAL";
	const isSelectedSiteContextReady = useMemo(() => {
		if (!selectedSite) return false;
		if (isGlobalSite) return true;
		if (!normalizedSelectedSiteData?.customer_site_number) return false;
		return String(normalizedSelectedSiteData.customer_site_number) === String(selectedSite);
	}, [selectedSite, isGlobalSite, normalizedSelectedSiteData]);

	useEffect(() => {
		const previousSelectedSite = previousSelectedSiteRef.current;
		const siteChanged = Boolean(previousSelectedSite && selectedSite && String(previousSelectedSite) !== String(selectedSite));
		previousSelectedSiteRef.current = selectedSite;

		if (
			siteChanged &&
			(
				selectedSiteLineId ||
				new URLSearchParams(location.search).has("line_name") ||
				new URLSearchParams(location.search).has("line_number")
			)
		) {
			navigate(ROUTES.MACHINES, { replace: true });
		}
	}, [selectedSite, selectedSiteLineId, location.search, navigate]);

	useEffect(() => {
		setSiteLines([]);
		setMachines([]);
		setLineDocumentationById({});
		setActiveSiteLineId(null);
		setLineSelectionMode(true);
		setCurrentPage(1);
		setLinesPage(1);
		setLineSearchTerm("");
		setMachineSearchTerm("");
	}, [selectedSite, normalizedSelectedSiteData?.site_id, normalizedSelectedSiteData?.cus_id]);

	useEffect(() => {
		setCurrentPage(1);
		setMachineSearchTerm("");
		if (selectedSiteLineId) {
			setActiveSiteLineId(selectedSiteLineId);
			setLineSelectionMode(false);
			return;
		}
		setActiveSiteLineId(null);
		setLineSelectionMode(true);
		setMachines([]);
	}, [selectedSiteLineId]);

	useEffect(() => {
		if (!selectedSite || !isSelectedSiteContextReady) {
			return;
		}

		let cancelled = false;
		const fetchLines = async () => {
			try {
				setLoading(true);
				const response = await getSiteLines({
					site_number: selectedSite,
					site_id: normalizedSelectedSiteData?.site_id,
					cus_id: normalizedSelectedSiteData?.cus_id,
				});
				if (cancelled) return;
				if (response?.status) {
					setSiteLines(Array.isArray(response.data) ? response.data : []);
					setError(null);
				} else {
					setSiteLines([]);
					setError(t('ERRORS.FAILED_TO_FETCH_MACHINE_DATA'));
				}
			} catch (err) {
				if (!cancelled) {
					setSiteLines([]);
					setError(t('ERRORS.AN_ERROR_OCCURRED_WHILE_FETCHING_DATA') + ": " + err.message);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		fetchLines();

		return () => {
			cancelled = true;
		};
	}, [selectedSite, isSelectedSiteContextReady, normalizedSelectedSiteData?.site_id, normalizedSelectedSiteData?.cus_id, t]);

	useEffect(() => {
		const siteId = Number(normalizedSelectedSiteData?.site_id);
		if (!isSelectedSiteContextReady || !Number.isFinite(siteId) || siteId <= 0 || siteLines.length === 0) {
			setLineDocumentationById({});
			return;
		}

		let cancelled = false;
		const cusId = normalizedSelectedSiteData?.cus_id;

		const fetchLineDocumentation = async () => {
			const entries = await Promise.all(
				siteLines.map(async (line) => {
					const siteLineId = line?.site_line_id;
					if (!siteLineId) return [siteLineId, false];
					try {
						const assignedMachineIds = await getAssignedMachineIdsForSite(siteId, cusId, siteLineId);
						return [siteLineId, assignedMachineIds.size > 0];
					} catch (error) {
						console.warn("Failed to resolve line documentation availability:", error);
						return [siteLineId, false];
					}
				})
			);

			if (cancelled) return;
			setLineDocumentationById(
				entries.reduce((acc, [siteLineId, hasDocumentation]) => {
					if (siteLineId) acc[siteLineId] = hasDocumentation;
					return acc;
				}, {})
			);
		};

		fetchLineDocumentation();

		return () => {
			cancelled = true;
		};
	}, [siteLines, isSelectedSiteContextReady, normalizedSelectedSiteData?.site_id, normalizedSelectedSiteData?.cus_id]);

	const handlePartsQuoteTemplateDownload = async () => {
		try {
			const templateUrl = `${ASSETS_BASE_URL}/Templates/partsQuoteTemplate.csv`;
			const response = await fetch(templateUrl);
			if (!response.ok) {
				window.open(templateUrl, "_blank");
				return;
			}
			const blob = await response.blob();
			const objectUrl = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = objectUrl;
			link.setAttribute("download", "partsQuoteTemplate.csv");
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(objectUrl);
		} catch (_error) {
			window.open(`${ASSETS_BASE_URL}/Templates/partsQuoteTemplate.csv`, "_blank");
		}
	};

	useEffect(() => {
		if (!selectedSite || lineSelectionMode || !activeSiteLineId) {
			setMachines([]);
			setError(null);
			return;
		}

		if (!isSelectedSiteContextReady) {
			return;
		}

		// Determine if this is initial load or search/filter operation
		const isInitialLoad = loading && !machines.length;
		const isSearchOrFilter = !isInitialLoad;

		if (isSearchOrFilter) {
			setIsSearching(true);
		} else {
			setLoading(true);
		}

		const fetchMachines = async () => {
			try {
				// The request now sends all parameters to the backend
				const reqBody = {
					site_id: normalizedSelectedSiteData?.site_id,
					cus_id: normalizedSelectedSiteData?.cus_id,
					site_line_id: activeSiteLineId,
					page: currentPage,
					limit: pageSize,
					searchTerm: debouncedSearchTerm,
					sortBy: sortOption,
				};

				const response = await getMachinesWithModels({
					...reqBody,
					includeModelData: false,
					includeBomCount: false,
				});

				if (response.status) {
					// State is set directly from the paginated API response
					const machinesWithFlags = (response.data || []).map((machine) => ({
						...machine,
						site_line_id: machine.site_line_id || activeSiteLineId,
						model_data_loaded: Boolean(machine.model_data),
					}));
					const lines = response.pagination?.siteLines || [];
					setMachines(machinesWithFlags);
					if (lines.length > 0) setSiteLines(lines);
					setTotalPages(response.pagination.totalPages);
					setError(null); // Clear any previous errors
				} else {
					setError(t('ERRORS.FAILED_TO_FETCH_MACHINE_DATA'));
				}
			} catch (err) {
				setError(t('ERRORS.AN_ERROR_OCCURRED_WHILE_FETCHING_DATA') + ": " + err.message);
			} finally {
				setLoading(false);
				setIsSearching(false);
			}
		};

		fetchMachines();
	}, [currentPage, pageSize, selectedSite, debouncedSearchTerm, sortOption, activeSiteLineId, lineSelectionMode, isSelectedSiteContextReady, normalizedSelectedSiteData, t]);

	useEffect(() => {
		if (!machines.length) {
			setIconsLoadingById({});
			return;
		}

		const idsToLoad = machines
			.filter((machine) => machine?.machine_id && !machine.model_data_loaded)
			.map((machine) => machine.machine_id);

		if (!idsToLoad.length) return;

		setIconsLoadingById((prev) => {
			const next = { ...prev };
			idsToLoad.forEach((id) => {
				next[id] = true;
			});
			return next;
		});

		let canceled = false;
		const loadIcons = async () => {
			try {
				const response = await getMachinesByIds({
					machines: idsToLoad,
					site_line_id: activeSiteLineId,
					site_number: selectedSite,
					site_id: normalizedSelectedSiteData?.site_id,
					cus_id: normalizedSelectedSiteData?.cus_id,
				});
				if (!response?.status || canceled) return;
				const fetched = response.data || [];
				const fetchedById = new Map(
					fetched.map((machine) => [machine.machine_id, machine])
				);

				setMachines((prev) =>
					prev.map((machine) => {
						const updated = fetchedById.get(machine.machine_id);
						if (!updated) return machine;
						return {
							...machine,
							...updated,
							site_line_id: updated.site_line_id || machine.site_line_id,
							model_data_loaded: true,
						};
					})
				);
			} catch (err) {
				// Keep cards visible even if icon data fails
				console.warn("Failed to load machine icons:", err);
			} finally {
				if (canceled) return;
				setIconsLoadingById((prev) => {
					const next = { ...prev };
					idsToLoad.forEach((id) => {
						delete next[id];
					});
					return next;
				});
			}
		};

		const idleHandle = setTimeout(loadIcons, 0);

		return () => {
			canceled = true;
			clearTimeout(idleHandle);
		};
	}, [machines, activeSiteLineId, selectedSite, normalizedSelectedSiteData?.site_id, normalizedSelectedSiteData?.cus_id]);

	const handleSortChange = (value) => {
		setCurrentPage(1); // Reset to page 1 when sorting
		setSortOption(value);
	};

	const handleMachineSearchChange = (value) => {
		setCurrentPage(1); // Reset to page 1 when searching
		setMachineSearchTerm(value);
	};

	const handleLineChange = (siteLineId, lineName, lineNumber) => {
		setCurrentPage(1);
		setMachineSearchTerm("");
		setLineSearchTerm("");
		setActiveSiteLineId(siteLineId);
		setLineSelectionMode(false);
		navigate(`${ROUTES.MACHINES}${buildLineQuery(siteLineId, lineName, lineNumber)}`);
	};

	const handleBackToLines = () => {
		setLineSelectionMode(true);
		setActiveSiteLineId(null);
		setMachines([]);
		setMachineSearchTerm("");
		setCurrentPage(1);
		navigate(ROUTES.MACHINES);
	};

	const buildLineQuery = (siteLineId, lineName, lineNumber) => {
		const params = new URLSearchParams();
		if (siteLineId) params.set("site_line_id", siteLineId);
		if (lineName) params.set("line_name", lineName);
		if (lineNumber) params.set("line_number", lineNumber);
		const query = params.toString();
		return query ? `?${query}` : "";
	};

	const handleLineDocuments = (event, siteLineId, lineName, lineNumber) => {
		event.stopPropagation();
		navigate(`/documents${buildLineQuery(siteLineId, lineName, lineNumber)}`);
	};

	const handleLineOrders = (event, siteLineId, lineName, lineNumber) => {
		event.stopPropagation();
		navigate(`/order-history${buildLineQuery(siteLineId, lineName, lineNumber)}`);
	};

	const handlePageSizeChange = (event) => {
		setCurrentPage(1);
		setPageSize(Number(event.target.value));
	};

	// Show full-page loading only on initial load
	if (loading && machines.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	// Show full-page error only if no data was loaded previously
	if (error && machines.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="error" className="error-alert">
					{error}
				</Alert>
			</Box>
		);
	}

	return (
		<div className="machines-page">
			<PageTitle title={lineSelectionMode ? (machines.length > 0 ? `${t('LABELS.MY_LINES', { defaultValue: 'My Lines' })} (${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, (currentPage - 1) * pageSize + machines.length)})` : t('LABELS.MY_LINES', { defaultValue: 'My Lines' })) : t('LABELS.MACHINES')} />

			<Box sx={{ width: "100%", display: 'flex', justifyContent: 'space-between', alignItems: 'center', columnGap: 2, mb: 2 }}>
				{/* Add data-tour to the legend/info box */}
				<Box sx={{ flex: 1, width: "100%" }} data-tour="product-3d-model">
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								flexWrap: "wrap",
								columnGap: 0.75,
								rowGap: 0.4,
								color: "#aaa",
								fontSize: "11px",
							}}
						>
							<Box component="span" sx={{ fontWeight: 700, color: "#999" }}>
								Index:
							</Box>
							{lineSelectionMode ? (
								<>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<DescriptionOutlinedIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.DOCUMENTATIONS")}
									</Box>
									<Box component="span">|</Box>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<InventoryIcon fontSize="small" sx={{ mx: "3px" }} />: {t("LABELS.ORDER")}
									</Box>
								</>
							) : (
								<>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<ModelIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.MACHINE_3D_MODEL")}
									</Box>
									<Box component="span">|</Box>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<ModelPDFIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.TWOD_MODEL")}
									</Box>
									<Box component="span">|</Box>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<DescriptionOutlinedIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.DOCUMENTATIONS")}
									</Box>
									<Box component="span">|</Box>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<InventoryIcon fontSize="small" sx={{ mx: "3px" }} />: {t("LABELS.ORDER")}
									</Box>
									<Box component="span">|</Box>
									<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
										<PiTreeStructureFill size={14} style={{ margin: "0 3px" }} />: {t("UI_TEXT.VIEW_BOM")}
									</Box>
								</>
							)}
						</Box>
					</Box>
				<Box sx={{ width: "100%", flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap: 2 }}>
						{!lineSelectionMode && (
							<Button
								variant="text"
								color="primary"
								size="small"
								startIcon={<Download />}
								onClick={handlePartsQuoteTemplateDownload}
							>
								{t('TITLES.PARTS_ORDER_TEMPLATE')}
							</Button>
						)}
					{lineSelectionMode && siteLines.length > 0 && (
						<Box sx={{ width: "140px" }}>
							<TextField
								select
								size="small"
								label={t('GENERAL.PER_PAGE')}
								value={linesPageSize}
								onChange={(e) => { setLinesPage(1); setLinesPageSize(Number(e.target.value)); }}
								fullWidth
								SelectProps={{ MenuProps: { disableScrollLock: true } }}
							>
								{pageSizeOptions.map((size) => (
									<MenuItem key={size} value={size}>{size}</MenuItem>
								))}
							</TextField>
						</Box>
					)}
					{lineSelectionMode && (
						<Box sx={{ maxWidth: '300px', width: '300px' }}>
							<TextField
								size="small"
								fullWidth
								placeholder="Search Lines"
								value={lineSearchTerm}
								onChange={(e) => { setLinesPage(1); setLineSearchTerm(e.target.value); }}
								sx={{ background: "#fff", borderRadius: 2 }}
							/>
						</Box>
					)}
					{!lineSelectionMode && (
						<>
							{/* <Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<ArrowBackIcon />}
								onClick={handleBackToLines}
							>
								Lines
							</Button> */}
							<Box sx={{ width: "140px" }}>
								<TextField
									select
									size="small"
									label={t('GENERAL.PER_PAGE')}
									value={pageSize}
									onChange={handlePageSizeChange}
									fullWidth
									SelectProps={{
										MenuProps: {
											disableScrollLock: true,
										},
									}}
								>
									{pageSizeOptions.map((size) => (
										<MenuItem key={size} value={size}>
											{size}
										</MenuItem>
									))}
								</TextField>
							</Box>
							<Box sx={{ maxWidth: '300px', width: '300px' }} data-tour="search-machine">
								<ContextFilterAndSort
									value={machineSearchTerm}
									onSearchChange={handleMachineSearchChange}
									placeholder={t('PLACEHOLDER.SEARCH_MACHINES')}
									options={sortOptions}
									selectedOption={sortOption}
									onSortChange={handleSortChange}
									showSearch={true}
									showSort={true}
									disabled={isSearching}
								/>
							</Box>
						</>
					)}
				</Box>
			</Box>

			{lineSelectionMode && (
				<>
				<Box className="machine-line-cards" data-tour="machine-lines">
					{paginatedLines.map((line) => {
						const siteLabel = [line.customer_name, line.customer_site_name].filter(Boolean).join(" / ");
						const showLineDocumentationIcon = Boolean(lineDocumentationById[line.site_line_id]);
						const showLineQuoteIcon = line?.has_quote_history !== false;
						return (
							<Card
								key={line.site_line_id}
								className="machine-card machine-line-card"
							>
								<CardActionArea onClick={() => handleLineChange(line.site_line_id, line.line_name, line.line_number)} className="machine-card-content">
									<CardMedia
										component="img"
										image={lineFallbackImage}
										alt={line.line_name}
										loading="lazy"
										className="machine-img"
										sx={{ objectFit: "contain", p: 2 }}
										onError={(e) => {
											e.target.src = lineFallbackImage;
										}}
									/>
									<CardContent sx={{ paddingBottom: "10px", textAlign: "center" }}>
										<Tooltip title={line.line_name || ""}>
											<Typography
												variant="body1"
												sx={{
													fontWeight: "bold",
													lineHeight: "1.2rem",
													marginBottom: "5px",
													display: "-webkit-box",
													overflow: "hidden",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													textOverflow: "ellipsis",
													minHeight: "2.4rem",
													color: "#000",
												}}
											>
												{line.line_name}
											</Typography>
										</Tooltip>
										{siteLabel && (
											<Tooltip title={siteLabel}>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{
														fontSize: "11px",
														display: "-webkit-box",
														overflow: "hidden",
														WebkitLineClamp: 1,
														WebkitBoxOrient: "vertical",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{siteLabel}
												</Typography>
											</Tooltip>
										)}
										<Box>
											<div className="action-buttons">
												{showLineDocumentationIcon && (
													<Tooltip title={t("UI_TEXT.DOCUMENTATIONS")}>
														<IconButton
															aria-label="Documentation"
															size="large"
															onClick={(event) => handleLineDocuments(event, line.site_line_id, line.line_name, line.line_number)}
														>
															<DescriptionOutlinedIcon />
														</IconButton>
													</Tooltip>
												)}
												{showLineQuoteIcon && (
												<Tooltip title={t("TITLES.VIEW_ORDER_HISTORY")}>
													<IconButton
														aria-label="Order History"
														size="large"
														onClick={(event) => handleLineOrders(event, line.site_line_id, line.line_name, line.line_number)}
													>
														<InventoryIcon />
													</IconButton>
												</Tooltip>
												)}
											</div>
										</Box>
									</CardContent>
								</CardActionArea>
							</Card>
						);
					})}
					{siteLines.length === 0 && (
						<Alert severity="info" sx={{ width: "fit-content" }}>
							{t('ALERT_MESSAGES.NO_LINES_FOUND_FOR_THE_SELECTED_SITE')}
						</Alert>
					)}
				</Box>
				{linesTotalPages > 1 && (
					<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
						<Pagination
							count={linesTotalPages}
							page={linesPage}
							onChange={(e, page) => setLinesPage(page)}
							sx={{
								"& .Mui-selected": {
									backgroundColor: "var(--primary-main)",
									color: "white",
									background: "var(--primary-main)",
								},
							}}
						/>
					</Box>
				)}
				</>
			)}

			{/* Add data-tour to the machines list container */}
			{!lineSelectionMode && (
			<div className="machines-list-container" data-tour="machines-list">
				{error && machines.length > 0 && (
					<Alert severity="error" sx={{ marginBottom: 2 }}>
						{error}
					</Alert>
				)}

				{isSearching && (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
						<CircularProgress size={30} />
						<Typography sx={{ ml: 2 }}>{t('UI_TEXT.SEARCHING')}</Typography>
					</Box>
				)}

				{!isSearching && (
					<>
						{machines.length === 0 ? (
							<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
								<Alert severity="info" sx={{ width: "fit-content" }}>
									{debouncedSearchTerm ?
										`No machines found matching "${debouncedSearchTerm}"` :
										t('ALERT_MESSAGES.NO_MACHINES_FOUND_FOR_THE_SELECTED_SITE')
									}
								</Alert>
							</Box>
						) : (
							machines.map((machine, index) => (
								<div key={machine.machine_id} className="machine-card-slot">
									{/* Only add data-tour to the first machine card */}
									<MachineCard
										product={machine}
										iconsLoading={Boolean(iconsLoadingById[machine.machine_id])}
										data-tour={index === 0 ? "machine-card" : undefined}
									/>
								</div>
							))
						)}
					</>
				)}
			</div>
			)}

			{!lineSelectionMode && !loading && !error && !isSearching && totalPages > 1 && (
				<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={(e, page) => setCurrentPage(page)}
						sx={{
							"& .Mui-selected": {
								backgroundColor: "var(--primary-main)",
								color: "white",
								background: "var(--primary-main)",
							},
						}}
					/>
				</Box>
			)}
		</div>
	);
};

export default MyMachines;
